"use client";

import Link from "next/link";
import type { ComponentType } from "react";
import Settings2 from "lucide-react/dist/esm/icons/settings-2";
import Shield from "lucide-react/dist/esm/icons/shield";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * "What can I do to this thing, from here?"
 *
 * The asset page grew a `canControl = isOwner || isAdmin` Manage dropdown and it
 * works well — but nothing else had one, so managing a store, a commission or a
 * review meant leaving the page for /admin or /seller and finding the row again.
 * This is that same affordance, extracted so every entity surface offers it in
 * the same place, with the same words, at the same weight.
 *
 * Two rules it enforces so the pattern can't drift:
 *   - Owner actions come first, admin actions last, always under an "Admin"
 *     heading. An admin looking at their own listing should never wonder which
 *     hat an action is being taken with.
 *   - Destructive items are tinted and pushed to the bottom of their group.
 *
 * Render nothing when there are no actions — callers can mount it
 * unconditionally and let the permission checks decide.
 */

export interface ContextAction {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** Internal route. Mutually exclusive with `onSelect`. */
  href?: string;
  onSelect?: () => void;
  /** Tints the item and sorts it to the end of its group. */
  destructive?: boolean;
  disabled?: boolean;
  /** Small muted line under the label — use for consequences, not decoration. */
  description?: string;
}

interface ContextActionMenuProps {
  /** Actions the viewer can take because the thing is theirs. */
  ownerActions?: ContextAction[];
  /** Actions the viewer can take because they're staff. Always grouped last. */
  adminActions?: ContextAction[];
  /** Trigger label. Keep it a verb the viewer would use ("Manage", "Moderate"). */
  label?: string;
  align?: "start" | "end";
  size?: "sm" | "default";
  className?: string;
  /** Accessible name when the trigger is icon-only. */
  triggerAriaLabel?: string;
}

function sortActions(actions: ContextAction[]): ContextAction[] {
  return [...actions].sort((a, b) => Number(!!a.destructive) - Number(!!b.destructive));
}

function ActionItem({ action }: { action: ContextAction }) {
  const Icon = action.icon;
  const inner = (
    <>
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate">{action.label}</span>
        {action.description && (
          <span className="block text-2xs text-muted-foreground">{action.description}</span>
        )}
      </span>
    </>
  );

  const className = cn("gap-2", action.destructive && "text-destructive focus:text-destructive");

  if (action.href && !action.disabled) {
    return (
      <DropdownMenuItem asChild className={className}>
        <Link href={action.href}>{inner}</Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem onClick={action.onSelect} disabled={action.disabled} className={className}>
      {inner}
    </DropdownMenuItem>
  );
}

export function ContextActionMenu({
  ownerActions = [],
  adminActions = [],
  label = "Manage",
  align = "end",
  size = "sm",
  className,
  triggerAriaLabel,
}: ContextActionMenuProps) {
  const owner = sortActions(ownerActions.filter(Boolean));
  const admin = sortActions(adminActions.filter(Boolean));

  // Nothing to offer — render nothing rather than a dead button.
  if (owner.length === 0 && admin.length === 0) return null;

  const adminOnly = owner.length === 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          aria-label={triggerAriaLabel}
          className={cn(
            "shrink-0 gap-1.5",
            // Staff-only menus are tinted so it's obvious at a glance that what
            // follows is a moderator power, not something every visitor sees.
            adminOnly && "border-warning/40 text-warning hover:bg-warning/10 hover:text-warning",
            className,
          )}
        >
          {adminOnly ? <Shield className="h-3.5 w-3.5" /> : <Settings2 className="h-3.5 w-3.5" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-60">
        {owner.map((action) => (
          <ActionItem key={action.id} action={action} />
        ))}

        {owner.length > 0 && admin.length > 0 && <DropdownMenuSeparator />}

        {admin.length > 0 && (
          <>
            <DropdownMenuLabel className="flex items-center gap-1.5 text-warning">
              <Shield className="h-3.5 w-3.5" />
              Admin
            </DropdownMenuLabel>
            {admin.map((action) => (
              <ActionItem key={action.id} action={action} />
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ContextActionMenu;
